import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { ConfigService } from './config.service';
import { ApiDetailQuittance } from '../models/devis-api.models';
import { ApiInitData } from '../models/init-data.models';
import { ApiConfigService } from './api-config.service';

@Injectable({ providedIn: 'root' })
export class SouscriptionApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly configService: ConfigService,
  ) {}

  /** POST /api-souscription{suffixe}/souscription/v1/devis */
  fetchDevis(dto: Record<string, unknown>): Observable<ApiDetailQuittance[]> {
    const url = this.apiConfig.devisUrl;
    return this.http.post<ApiDetailQuittance[]>(url, dto).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] devis', url, response);
        }
      }),
    );
  }

  /** POST /api-souscription{suffixe}/souscription/v1/afn */
  fetchAfn(dto: Record<string, unknown>): Observable<ApiDetailQuittance> {
    const url = this.apiConfig.afnUrl;
    return this.http.post<ApiDetailQuittance>(url, dto).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] afn', url, response);
        }
      }),
    );
  }

  /** POST /api-souscription{suffixe}/souscription/v1/afnv */
  fetchAfnv(dto: Record<string, unknown>): Observable<ApiDetailQuittance> {
    const url = this.apiConfig.afnvUrl;
    return this.http.post<ApiDetailQuittance>(url, dto).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] afnv', url, response);
        }
      }),
    );
  }

  /** POST /api-souscription{suffixe}/souscription/v1/initdata */
  fetchInitData(): Observable<ApiInitData> {
    const url = this.apiConfig.initDataUrl;
    return this.http.post<ApiInitData>(url, {}).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] initdata', url, response);
        }
      }),
    );
  }

  /** POST /api-souscription{suffixe}/policy/v1/encaissement/versement */
  handleFreePayment(dto: Record<string, unknown>): Observable<unknown> {
    const url = this.apiConfig.policyFreePaymentUrl;
    return this.http.post<unknown>(url, dto).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] free payment', url, response);
        }
      }),
    );
  }

  /** POST /api-souscription{suffixe}/policy/v1/encaissement/terme */
  handlePeriodicPayment(dto: Record<string, unknown>): Observable<unknown> {
    const url = this.apiConfig.policyPeriodicPaymentUrl;
    return this.http.post<unknown>(url, dto).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] periodic payment', url, response);
        }
      }),
    );
  }

  /** POST /api-souscription{suffixe}/policy/v1/life/consult — retourne `totaEnca`. */
  consultLifePolicy(dto: Record<string, string>): Observable<number> {
    const url = this.apiConfig.policyLifeConsultUrl;
    return this.http.post<unknown>(url, dto).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] life consult', url, response);
        }
      }),
      map((raw) => this.parseTotaEnca(raw)),
    );
  }

  private parseTotaEnca(raw: unknown): number {
    if (!raw || typeof raw !== 'object') {
      return 0;
    }
    const body = raw as Record<string, unknown>;
    const data = body['data'] && typeof body['data'] === 'object' ? (body['data'] as Record<string, unknown>) : body;
    const police =
      data['police'] && typeof data['police'] === 'object' ? (data['police'] as Record<string, unknown>) : data;
    const value = police['totaEnca'] ?? police['TOTAENCA'] ?? police['totaenca'] ?? 0;
    const parsed = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /** POST /api-souscription{suffixe}/souscription/v1/print/{idenetat}/{codeinte}/{numepoli} */
  fetchPrint(idenetat: string, codeinte: string, numepoli: string): Observable<string> {
    const url = this.apiConfig.buildPrintUrl(idenetat, codeinte, numepoli);
    return this.http.post(url, {}, { responseType: 'text' }).pipe(
      tap((response) => {
        if (this.configService.enableApiLogs) {
          console.log('[SouscriptionApi] print', url, `${response?.length ?? 0} chars`);
        }
      }),
    );
  }
}
